import { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import {
  cancelSubscriptionCheckout,
  purchaseSubscription,
} from '../api/subscriptions';
import type { SubscriptionPeriod } from '../components/subscription/SubscriptionCard';
import { useAuth } from '../context/AuthContext';
import type { PlanId } from '../types/subscription';

export function useSubscriptionCheckout({
  returningSubscriber = false,
}: { returningSubscriber?: boolean } = {}) {
  const { user, company, hasPermission, loading } = useAuth();
  const navigate = useNavigate();
  const companyId = company?.id;
  const canManage =
    hasPermission('canManageSubscription') && company?.ownerId === user?.uid;
  const [cancelId, setCancelId] = useState(() =>
    new URLSearchParams(window.location.search).get('cancel_checkout_id')
  );
  const [state, setState] = useState<
    'idle' | 'checkout' | 'waiting' | 'canceling' | 'blocked' | 'error'
  >(cancelId ? 'waiting' : 'idle');
  const [error, setError] = useState<string | null>(null);
  const busy = useRef(false);
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    if (!cancelId) return;
    if (loading) {
      setState('waiting');
      setError(null);
      return;
    }
    if (!companyId || !canManage) {
      setState('blocked');
      setError(
        !companyId
          ? 'Select the company used for checkout to continue.'
          : 'Only the company owner can cancel this checkout. Switch to the owner account or company to continue.'
      );
      return;
    }
    let disposed = false;
    setState('canceling');
    setError(null);
    void cancelSubscriptionCheckout(companyId, cancelId)
      .then(() => {
        if (disposed) return;
        const params = new URLSearchParams(window.location.search);
        params.delete('cancel_checkout_id');
        navigate(
          `${window.location.pathname}${params.size ? '?' + params.toString() : ''}`,
          { replace: true }
        );
        setCancelId(null);
        setState('idle');
      })
      .catch((cause: unknown) => {
        if (disposed) return;
        setError(
          cause instanceof Error
            ? cause.message
            : 'Unable to cancel checkout. Please try again.'
        );
        setState('error');
      });
    return () => {
      disposed = true;
    };
  }, [cancelId, companyId, canManage, loading, retry, navigate]);

  const subscribe = async (plan: PlanId, period: SubscriptionPeriod) => {
    if (busy.current || cancelId || !hasPermission('canManageSubscription'))
      return;
    if (!user || !company || company.ownerId !== user.uid) return;
    busy.current = true;
    setError(null);
    setState('checkout');
    try {
      sessionStorage.setItem(
        `billing-returning-${company.id}`,
        String(
          returningSubscriber ||
            company.subscription?.hasSubscribed === true ||
            !!company.subscription?.tier
        )
      );
      await purchaseSubscription(company.id, plan, period);
      // Keep checkout blocked while the browser leaves for Stripe.
    } catch (cause) {
      const message =
        cause instanceof Error
          ? cause.message
          : 'Unable to open checkout. Please try again.';
      setError(message);
      toast.error(message, { id: 'subscription-checkout', duration: 6000 });
      setState('error');
      busy.current = false;
    }
  };
  return {
    state,
    error,
    subscribe,
    retryCancellation: () => setRetry((value) => value + 1),
    cancellationPending: !!cancelId,
    buttonLabel:
      state === 'checkout'
        ? 'Opening…'
        : state === 'canceling'
          ? 'Canceling checkout…'
          : state === 'waiting'
            ? 'Loading company…'
            : undefined,
    busy:
      state === 'checkout' ||
      state === 'canceling' ||
      state === 'waiting' ||
      (!!cancelId && canManage),
  };
}
